import { Controller } from "@hotwired/stimulus";

// Handles restaurant search via Google Places during onboarding.
// Live autocomplete with debounce — results appear as the user types.
// Auto-fills form fields when the user selects a result.
export default class extends Controller {
  static targets = [
    "query",
    "results",
    "resultsList",
    "nameInput",
    "cityInput",
    "addressInput",
    "phoneInput",
    "cuisineInput",
    "websiteInput",
    "baseLanguageInput",
    "manualHint",
    "countryInput",
    "googlePlaceIdInput",
    "zipCodeInput",
    "stateInput",
    "latitudeInput",
    "longitudeInput",
    "googleRatingInput",
    "googleRatingsCountInput",
    "openingHoursInput",
    "currencyInput",
  ];
  static values = {
    url: String,
    debounce: { type: Number, default: 300 },
    minChars: { type: Number, default: 3 },
    countryDefaults: { type: Object, default: {} },
  };

  connect() {
    this.#debounceTimer = null;
  }

  disconnect() {
    clearTimeout(this.#debounceTimer);
  }

  onInput() {
    clearTimeout(this.#debounceTimer);

    const query = this.queryTarget.value.trim();

    if (query.length < this.minCharsValue) {
      this.#hideResults();
      return;
    }

    this.#debounceTimer = setTimeout(
      () => this.#fetchResults(query),
      this.debounceValue,
    );
  }

  select(event) {
    const data = JSON.parse(event.currentTarget.dataset.place);

    this.#fillIfEmpty(this.nameInputTarget, data.name);
    this.#fillIfEmpty(this.cityInputTarget, data.city);
    this.#fillIfEmpty(this.addressInputTarget, data.address);
    this.#fillIfEmpty(this.phoneInputTarget, data.phone);
    this.#fillIfEmpty(this.cuisineInputTarget, data.cuisine_type);
    this.#fillIfEmpty(this.websiteInputTarget, data.website);
    this.#selectLanguage(data.language);

    this.#fill(this.googlePlaceIdInputTarget, data.google_place_id);
    this.#fill(this.zipCodeInputTarget, data.zip_code);
    this.#fill(this.stateInputTarget, data.state);
    this.#fill(this.latitudeInputTarget, data.latitude);
    this.#fill(this.longitudeInputTarget, data.longitude);
    this.#fill(this.googleRatingInputTarget, data.google_rating);
    this.#fill(this.googleRatingsCountInputTarget, data.google_ratings_count);
    this.#fill(
      this.openingHoursInputTarget,
      data.opening_hours ? JSON.stringify(data.opening_hours) : null,
    );
    this.#selectOption(this.countryInputTarget, data.country);
    this.#applyCountryDefaults(data.country);

    this.queryTarget.value = "";
    this.#hideResults();
  }

  skipSearch() {
    this.#hideResults();
    this.nameInputTarget.focus();
  }

  onCountryChange() {
    const code = this.countryInputTarget.value;
    this.#applyCountryDefaults(code);
  }

  // Private

  #debounceTimer;

  async #fetchResults(query) {
    this.#showLoading();

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({ query }),
      });

      if (response.status === 429) {
        this.#showRateLimited();
        return;
      }

      if (!response.ok) {
        this.#showManualFallback();
        return;
      }

      const { results } = await response.json();
      this.#renderResults(results);
    } catch {
      this.#showManualFallback();
    }
  }

  #fillIfEmpty(input, value) {
    if (input && value && !input.value) {
      input.value = value;
    }
  }

  #selectLanguage(code) {
    if (!code || !this.hasBaseLanguageInputTarget) return;

    this.#selectOption(this.baseLanguageInputTarget, code);
  }

  #fill(input, value) {
    if (input && value != null) {
      input.value = value;
    }
  }

  #applyCountryDefaults(countryCode) {
    if (!countryCode) return;

    const defaults = this.countryDefaultsValue[countryCode];
    if (!defaults) return;

    this.#selectOption(this.currencyInputTarget, defaults.currency);
    this.#selectOption(this.baseLanguageInputTarget, defaults.base_language);
  }

  #selectOption(select, value) {
    if (!select || !value) return;

    const option = Array.from(select.options).find(
      (opt) => opt.value === value,
    );
    if (option) select.value = value;
  }

  #showLoading() {
    this.resultsTarget.classList.remove("hidden");
    this.resultsListTarget.innerHTML = `
      <div class="flex items-center gap-2 text-stone-400 py-3 px-3">
        <span class="loading loading-spinner loading-sm"></span>
        <span>${this.#escapeHtml(this.queryTarget.dataset.searchingText)}</span>
      </div>
    `;
  }

  #hideResults() {
    this.resultsTarget.classList.add("hidden");
    this.resultsListTarget.innerHTML = "";
  }

  #renderResults(results) {
    if (results.length === 0) {
      this.resultsListTarget.innerHTML = `
        <p class="text-stone-400 py-3 px-3">${this.#escapeHtml(this.queryTarget.dataset.noResultsText)}</p>
      `;
      return;
    }

    const mapsText = this.queryTarget.dataset.viewMapsText;

    this.resultsListTarget.innerHTML = results
      .map(
        (place) => `
      <button type="button"
              class="w-full text-left p-3 rounded-lg hover:bg-base-300
                     transition-colors cursor-pointer"
              data-action="click->places-lookup#select"
              data-place='${JSON.stringify(place).replace(/'/g, "&#39;")}'>
        <span class="font-medium text-base-content">${this.#escapeHtml(place.name)}</span>
        <span class="text-stone-400 text-sm block">${this.#escapeHtml(place.address || "")}</span>
        ${place.google_maps_url ? `<a href="${this.#escapeHtml(place.google_maps_url)}" target="_blank" rel="noopener noreferrer" class="text-primary text-xs hover:underline inline-block mt-1" onclick="event.stopPropagation()">${this.#escapeHtml(mapsText)}</a>` : ""}
      </button>
    `,
      )
      .join("");
  }

  #showRateLimited() {
    this.resultsListTarget.innerHTML = `
      <p class="text-stone-400 py-3 px-3">${this.#escapeHtml(this.queryTarget.dataset.rateLimitedText)}</p>
    `;
  }

  #showManualFallback() {
    this.resultsListTarget.innerHTML = `
      <p class="text-stone-400 py-3 px-3">${this.manualHintTarget.textContent}</p>
    `;
  }

  #escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
